import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { TeamMemberData } from '@/types/api';

const KEY = ['team'] as const;

/** Everyone with admin access, active or revoked. */
export function useTeam() {
  return useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<TeamMemberData[]> => {
      const { data } = await api.get<TeamMemberData[]>('/v1/team');
      return data;
    },
  });
}

/**
 * Revoke or restore someone's access. Admins are never deleted: they are named
 * on invoices, payments and journal entries, and removing the row would rewrite
 * the record of who did what.
 */
export function useSetTeamMemberAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      active,
    }: {
      id: number;
      active: boolean;
    }): Promise<TeamMemberData> => {
      await primeCsrf();
      const { data } = await api.post<TeamMemberData>(
        `/v1/team/${id}/${active ? 'reactivate' : 'deactivate'}`,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
